import React, { useEffect, useState } from 'react';
import Modal from 'react-modal';
import { FaSkullCrossbones, FaMusic } from 'react-icons/fa'; 
import logo from './logo.png'; 
import horrorSound from './horrer.mp3'; 
import horrorImage from './horror3.jpg'; 
import Questionnaire from './Questionnaire'; // Importing the Questionnaire component
import webmafiyaLogo from './logo-s.png'; // Import Webmafiya logo

const LandingPage = () => {
  const [text, setText] = useState('');
  const [name, setName] = useState(localStorage.getItem('name') || ''); // Retrieve saved name
  const [isModalOpen, setIsModalOpen] = useState(localStorage.getItem('isModalOpen') === 'true'); // Modal visibility from localStorage
  const [showQuestionnaire, setShowQuestionnaire] = useState(false); // To display the questionnaire
  const [isMusicPlaying, setIsMusicPlaying] = useState(true); // State to manage music playback
  const [isFollowPopupOpen, setIsFollowPopupOpen] = useState(false); // State for the follow popup

  const fullText = 'DEATH PREDICTOR';
  const description = "Discover your risk of early mortality based on your lifestyle and habits. Enter your name to begin your assessment.";

  useEffect(() => {
    const typingEffect = setInterval(() => {
      setText((prev) => {
        if (prev.length < fullText.length) {
          return fullText.slice(0, prev.length + 1);
        }
        clearInterval(typingEffect);
        return prev;
      });
    }, 200);
    return () => clearInterval(typingEffect);
  }, []);

  // Show follow popup after 5 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsFollowPopupOpen(true);
    }, 5000); // 5 seconds delay

    return () => clearTimeout(timer);
  }, []);

  // Handle form submit
  const handleSubmit = (e) => {
    e.preventDefault();
    const enteredName = e.target.name.value;
    setName(enteredName);
    setIsModalOpen(true); // Show the modal on submit
    localStorage.setItem('name', enteredName); // Save name to localStorage
    localStorage.setItem('isModalOpen', true); // Persist modal state
  };

  // Display the questionnaire
  const startQuestionnaire = () => {
    setShowQuestionnaire(true); // Show the questionnaire
  };

  // Close modal and reset state
  const closeModal = () => {
    setIsModalOpen(false);
    localStorage.setItem('isModalOpen', false); // Reset modal state in localStorage
  };

  // Close follow popup
  const closeFollowPopup = () => {
    setIsFollowPopupOpen(false);
  };

  // Confirm before leaving or refreshing the page
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = ''; // This triggers the browser's default confirmation dialog
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Function to toggle music
  const toggleMusic = () => {
    setIsMusicPlaying(prev => !prev);
  };

  return (
    <div className="flex items-center justify-center min-h-screen text-blue-600 relative">
      {/* Background sound */}
      {isMusicPlaying && (
        <audio autoPlay loop>
          <source src={horrorSound} type="audio/mp3" />
        </audio>
      )}

      {/* Full-width background image */}
      <div className="absolute inset-0">
        <img src={horrorImage} alt="Horror Background" className="w-full h-full object-cover" />
      </div>

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black opacity-20"></div>

      {/* Logo at top left */}
      <img src={logo} alt="Death Predictor Logo" className="absolute top-4 left-4 h-16 sm:h-32" />

      {/* Toggle music button */}
      <button 
        onClick={toggleMusic}
        className="absolute top-4 right-4 p-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
        aria-label={isMusicPlaying ? 'Stop Music' : 'Play Music'}
      >
        <FaMusic className={`${isMusicPlaying ? 'text-white' : 'text-gray-400'}`} />
      </button>

      {/* Main content */}
      <div className="relative z-10 text-center p-5 rounded-lg shadow-lg">
        {/* Animated heading */}
        <h1 className="text-3xl sm:text-5xl font-bold mb-4 animate-pulse glow-effect">
          <FaSkullCrossbones className="inline-block text-blue-600 mr-2" />
          {text}
        </h1>

        {/* Description */}
        <p className="mb-8 text-sm sm:text-base bg-black">{description}</p>
      </div>

      {/* Form container */}
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 z-10">
        <div className="relative p-5 rounded-lg shadow-lg">
          <form onSubmit={handleSubmit} className="flex flex-col items-center relative glow-effect z-12">
            <div className="flex flex-col sm:flex-row items-center">
              <input
                type="text"
                name="name"
                placeholder="Enter your name"
                className="px-4 py-2 mb-2 sm:mb-0 text-black rounded-lg mr-0 sm:mr-2" 
                defaultValue={name} // Pre-fill name if it exists in localStorage
                required
              />
              <button
                type="submit"
                className="px-4 py-2 mb-0 font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-500 transition-colors glow-effect"
              >
                Start
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal for greeting and questionnaire */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => {}} // No closing on outside click
        shouldCloseOnOverlayClick={false}
        contentLabel="Death Predictor Questionnaire"
        className="custom-modal bg-black bg-opacity-90 p-6 rounded-lg max-w-4xl mx-auto"
        overlayClassName="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center"
      >
        {!showQuestionnaire ? (
          <div className="text-white text-center">
            <h2 className="text-2xl mb-4">Dear {name},</h2>
            <p className="mb-4">We are about to see your lifespan percentage and assess your chances of dying earlier than the average. Are you ready to start?</p>
            <button
              onClick={startQuestionnaire}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Start Questionnaire
            </button>
          </div>
        ) : (
          <Questionnaire /> // Display the Questionnaire component
        )}

        {/* Close button */}
        <div className="mt-4 text-center">
          <button
            onClick={closeModal}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </Modal>

      {/* Follow Popup Modal */}
      <Modal
        isOpen={isFollowPopupOpen}
        onRequestClose={closeFollowPopup}
        className="custom-popup bg-white p-6 rounded-lg max-w-md mx-auto"
        overlayClassName="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center"
      >
        <div className="text-center">
          <img src={webmafiyaLogo} alt="Webmafiya Logo" className="mx-auto mb-4" />
          <h2 className="text-xl mb-2">Follow us on Instagram!</h2>
          <p className="mb-4">Stay updated with our latest content and announcements.</p>
          <a 
            href="https://www.instagram.com/webmafiya/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Follow Now
          </a>
        </div>
        <button
          onClick={closeFollowPopup}
          className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
        >
          Close
        </button>
      </Modal>
      <style jsx>{`
  .glow-effect {
    text-shadow: 
      0 0 5px rgba(0, 204, 255, 0.7),
      0 0 10px rgba(0, 204, 255, 0.7),
      0 0 20px rgba(0, 204, 255, 0.7),
      0 0 40px rgba(0, 204, 255, 0.7),
      0 0 80px rgba(0, 204, 255, 0.7);
  }

  .custom-modal {
    width: 90%;
    max-width: 400px; /* Limits modal width */
    height: auto;
    margin: 0 auto;
  }

  .custom-modal {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  @media (min-width: 640px) {
    .custom-modal {
      width: 100%; /* Larger width for larger screens */
      max-width: 100%; /* Limits modal width on larger screens */
    }
  }


 
`}</style>

    </div>
  );
};

export default LandingPage;
